import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Breadcrumb from '@/components/Breadcrumb';
import { BookOpen, ArrowRight } from 'lucide-react';
import { RESOURCE_ARTICLES } from '@/data/resourceArticles';

export default function ResourcesHub() {
  const items = RESOURCE_ARTICLES;
  const canonical = 'https://docsite.care/resources';
  const title = 'Resources for Doctors | SEO, Marketing & Website Growth | DocSite';
  const description = 'Evergreen guides for practicing doctors: SEO, patient-acquisition playbooks, website checklists, and how to build a practice that compounds online.';

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <section className="bg-gradient-to-br from-sky-50 via-white to-emerald-50 border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 md:py-16">
          <Breadcrumb items={[{ label: 'DocSite', to: '/' }, { label: 'Resources' }]} />
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mt-6 mb-3 flex items-center gap-1.5">
            <BookOpen size={12} /> Guides for doctors
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="resources-h1">
            Grow your practice online.
          </h1>
          <p className="text-base text-slate-600 mt-4 max-w-2xl">
            Evergreen, vendor-neutral guides on medical SEO, patient acquisition, and building a website that actually ranks on Google.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="grid md:grid-cols-2 gap-5" data-testid="resources-grid">
          {items.map(r => (
            <Link key={r.slug} to={`/resources/${r.slug}`}
              className="group rounded-2xl border border-slate-200 p-6 bg-white hover:border-sky-200 hover:shadow-md transition-all"
              data-testid={`resource-card-${r.slug}`}>
              <p className="text-xs uppercase tracking-wide font-bold text-sky-600 mb-2">Guide</p>
              <h3 className="text-lg font-medium text-slate-900 group-hover:text-sky-700 transition-colors leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {r.title}
              </h3>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{r.excerpt}</p>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {(r.keywords || []).slice(0, 3).map(k => (
                  <span key={k} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{k}</span>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-1 text-xs font-medium text-sky-600 group-hover:text-sky-700">
                Read guide <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
